<script lang="ts">
import { useInput, withCommonElInputProps } from '@/forms/input';

export const elRadioButtonSizes = ['xs', 'xxs'] as const;
type ElRadioButtonSizes = (typeof elRadioButtonSizes)[number];

export const elRadioButtonColors = ['primary', 'secondary', 'inactive'] as const;
type ElRadioButtonColors = (typeof elRadioButtonColors)[number];

const commonProp = withCommonElInputProps();
</script>

<script lang="ts" setup>
import { computed, PropType } from 'vue';

const props = defineProps({
  id: commonProp.id,
  disabled: commonProp.disabled,
  label: commonProp.label,
  validation: commonProp.validation,
  modelValue: {
    type: String,
    required: true,
  },
  size: {
    type: String as PropType<ElRadioButtonSizes>,
    default: 'xs',
    required: false,
  },
  name: {
    type: String,
    required: true,
  },
  value: {
    type: String,
    required: true,
  },
  color: {
    type: String as PropType<ElRadioButtonColors>,
    required: false,
    default: 'primary',
  },
});

const emit = defineEmits<{
  (event: 'update:modelValue', val: String): void;
}>();
const computedModelValue = computed(() => props.modelValue);
const computedValidation = computed(() => [props.validation]);
const { value: localValue, uuid } = useInput(computedModelValue, computedValidation, props.name, newLocalValue => {
  emit('update:modelValue', newLocalValue);
});
</script>

<template>
  <input
    :id="uuid"
    v-model="localValue"
    :name="name"
    :value="value"
    type="radio"
    class="sr-only"
    :disabled="disabled"
    :data-cy="label ? `${label.trim().replaceAll(' ', '')}-radio` : 'radio'"
  />

  <label :for="uuid" class="cursor-pointer flex items-start justify-center gap-2">
    <svg
      v-if="modelValue !== value"
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      class="flex-shrink-0"
      :class="{
        'w-6 h-6': size === 'xs',
        'w-5 h-5': size === 'xxs',
        'fill-neutral-inactive': color === 'inactive' || disabled,
        'fill-neutral-lighter': color !== 'inactive' && !disabled,
      }"
    >
      <path
        d="M10 0.5C4.48 0.5 0 4.98 0 10.5C0 16.02 4.48 20.5 10 20.5C15.52 20.5 20 16.02 20 10.5C20 4.98 15.52 0.5 10 0.5ZM10 18.5C5.58 18.5 2 14.92 2 10.5C2 6.08 5.58 2.5 10 2.5C14.42 2.5 18 6.08 18 10.5C18 14.92 14.42 18.5 10 18.5Z"
      />
    </svg>
    <svg
      v-if="modelValue === value"
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      class="flex-shrink-0"
      :class="{
        'w-6 h-6': size === 'xs',
        'w-5 h-5': size === 'xxs',
        'fill-neutral-inactive': color === 'inactive' || disabled,
        'fill-primary-active': color === 'primary',
        'fill-secondary-active': color === 'secondary',
      }"
    >
      <path
        d="M10 5.5C7.24 5.5 5 7.74 5 10.5C5 13.26 7.24 15.5 10 15.5C12.76 15.5 15 13.26 15 10.5C15 7.74 12.76 5.5 10 5.5ZM10 0.5C4.48 0.5 0 4.98 0 10.5C0 16.02 4.48 20.5 10 20.5C15.52 20.5 20 16.02 20 10.5C20 4.98 15.52 0.5 10 0.5ZM10 18.5C5.58 18.5 2 14.92 2 10.5C2 6.08 5.58 2.5 10 2.5C14.42 2.5 18 6.08 18 10.5C18 14.92 14.42 18.5 10 18.5Z"
      />
    </svg>

    <span
      v-if="label"
      :class="{
        'text-base': size === 'xs',
        'text-sm': size === 'xxs',
        'text-neutral-darker': color !== 'inactive' && !disabled,
        'text-neutral-inactive': disabled || color === 'inactive',
      }"
      >{{ label }}</span
    >
  </label>
</template>
